import React from "react"

import { ProductObj } from "scrivito/Objs/Product/ProductObjClass"
import { ScrivitoLink } from "scrivito/scrivito-link/ScrivitoLink"
import { isEmpty } from "shared/lib/arrayUtils"
import { useEvent } from "shared/lib/useEvent"
import { useRovingTabIndexGrid } from "shared/lib/useRovingTabIndexGrid"
import { PORTFOLIO_COLUMNS_BY_BREAKPOINT } from "shared/types/Breakpoints"
import { Button } from "shared/ui/buttons/Button"

import { ProductCardConnector } from "entities/products/ui/product-card/ProductCardConnector"

import "./styles/_product-grid.scss"

interface ProductGridProps {
  productObjs: ProductObj[] | null | undefined
  mode: "link" | "button"
  onSelect?: (productObj: ProductObj) => void
  isLoading?: boolean

  emptyMessage?: string
}

export const ProductGrid = React.memo(
  ({ productObjs, mode, emptyMessage, onSelect }: ProductGridProps) => {
    const CardComponent =
      mode === "link" ? ProductCategoryGridLink : ProductCategoryGridButton

    const { containerProps, getElementProps } = useRovingTabIndexGrid<
      HTMLElement,
      HTMLUListElement
    >({
      disableRovingTabIndex: true,
      elementCount: productObjs?.length ?? 0,
      isRoundRobin: false,
      columnsByBreakpoint: PORTFOLIO_COLUMNS_BY_BREAKPOINT
    })

    if (isEmpty(productObjs)) {
      return (
        <div className="product-grid">
          {emptyMessage ? (
            <div className="product-grid__empty-message">{emptyMessage}</div>
          ) : null}
        </div>
      )
    }

    return (
      <ul className="product-grid" {...containerProps}>
        {productObjs?.map((productObj, index) => (
          <li className="product-grid__item" key={productObj.id()}>
            <CardComponent
              item={productObj}
              onSelect={onSelect}
              {...getElementProps(index)}
            />
          </li>
        ))}
      </ul>
    )
  }
)

const ProductCategoryGridLink = React.forwardRef<
  HTMLElement,
  { item: ProductObj }
>(({ item, ...props }, ref) => {
  return (
    <ScrivitoLink
      className="anchor"
      to={item}
      {...props}
      ref={ref as React.Ref<HTMLAnchorElement>}
    >
      <ProductCardConnector productObj={item} />
    </ScrivitoLink>
  )
})

ProductCategoryGridLink.displayName = "ProductCategoryGridLink"

const ProductCategoryGridButton = React.forwardRef<
  HTMLElement,
  {
    item: ProductObj
    onSelect?: (category: ProductObj) => void
  }
>(({ item, onSelect, ...props }, ref) => {
  const handleSelect = useEvent(() => {
    onSelect?.(item)
  })

  return (
    <Button
      onClick={handleSelect}
      {...props}
      ref={ref as React.Ref<HTMLButtonElement>}
    >
      <ProductCardConnector productObj={item} />
    </Button>
  )
})

ProductCategoryGridButton.displayName = "ProductCategoryGridButton"
